import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { getAdmins, createAdminAccount, deleteAdminAccount } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface AdminUser {
  _id: string;
  username: string;
  fullName?: string;
  email?: string;
  role: string;
  createdBy?: string;
  createdAt: string;
}

function AdminManagement() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
  });

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<AdminUser | null>(null);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await getAdmins();
      if (response.success) {
        setAdmins(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching admins:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to load admin accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      const response = await createAdminAccount(formData);
      if (response.success) {
        setSuccessMsg(response.message || 'Admin created successfully!');
        setFormData({ username: '', password: '', fullName: '', email: '' });
        fetchAdmins();
      } else {
        setErrorMsg(response.message || 'Failed to create admin.');
      }
    } catch (err: any) {
      console.error('Error creating admin:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to create admin user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;
    try {
      const response = await deleteAdminAccount(adminToDelete._id);
      if (response.success) {
        setSuccessMsg(`Admin '${adminToDelete.username}' removed successfully.`);
        fetchAdmins();
      } else {
        setErrorMsg(response.message || 'Failed to delete admin.');
      }
    } catch (err: any) {
      console.error('Error deleting admin:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to delete admin user.');
    } finally {
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access Denied. Only Super Admin can manage admin accounts.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <AdminIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="bold">
          Admin Users Management
        </Typography>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {/* Create New Admin Form */}
      <Paper sx={{ p: 4, mb: 4 }} elevation={2}>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" display="flex" alignItems="center" gap={1}>
          <PersonAddIcon /> Add New Admin Account
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Passwords will be securely encrypted with bcrypt before storing into the MongoDB database admins table.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="e.g. jhon_doe"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter strong password"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Full Name (Optional)"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="e.g. John Doe"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="email"
                label="Email (Optional)"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@example.com"
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <PersonAddIcon />}
              sx={{ px: 4, py: 1.2, fontWeight: 'bold' }}
            >
              {submitting ? 'Creating Admin...' : 'Create Admin Account'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Existing Admins List */}
      <Paper sx={{ p: 3 }} elevation={2}>
        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
          Existing Database Admins ({admins.length})
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Username</strong></TableCell>
                  <TableCell><strong>Full Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell align="right"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No admin users found in the database. Add one above!
                    </TableCell>
                  </TableRow>
                ) : (
                  admins.map((admin) => (
                    <TableRow key={admin._id} hover>
                      <TableCell>{admin.username}</TableCell>
                      <TableCell>{admin.fullName || '-'}</TableCell>
                      <TableCell>{admin.email || '-'}</TableCell>
                      <TableCell>
                        <Chip label={admin.role} color="primary" size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{new Date(admin.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setAdminToDelete(admin);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Admin Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the admin account <strong>{adminToDelete?.username}</strong>? This user will no longer be able to log in to the admin panel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Admin
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminManagement;
